import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useScreen from "@/hooks/useScreen";
import { getMappingJurusanSekolah } from "@/services/getData";
import { JurusanSekolah, SysProgramStudi } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";

export default function ListProdi({
  dataProdi,
  isPending,
}: {
  dataProdi: {
    list_prodi_vokasi: SysProgramStudi[];
    list_prodi_sarjana: SysProgramStudi[];
    list_prodi: SysProgramStudi[];
  };
  isPending: boolean;
}) {
  const { isMobile } = useScreen();

  // State untuk kontrol accordion (bisa multiple open atau single)
  const [openSections, setOpenSections] = useState<string[]>(["SARJANA"]);

  const [selectedSysProdi, setSelectedSysProdi] =
    useState<SysProgramStudi | null>(null);
  const [showJurusanSekolah, setShowJurusanSekolah] = useState(false);
  const [searchQueryJurusan, setSearchQueryJurusan] = useState("");

  const [searchQueryProdi, setSearchQueryProdi] = useState("");

  const filteredProdiVokasi = dataProdi?.list_prodi_vokasi?.filter((prodi) =>
    prodi.unitkerja?.nama
      ?.toLocaleLowerCase()
      .includes(searchQueryProdi.toLocaleLowerCase()),
  );

  const filteredProdiSarjana = dataProdi?.list_prodi_sarjana?.filter((prodi) =>
    prodi.unitkerja?.nama
      ?.toLocaleLowerCase()
      .includes(searchQueryProdi.toLocaleLowerCase()),
  );

  const {
    data: listDataMappingJurusanSekolah,
    isPending: isPendingListDataMappingJurusanSekolah,
  } = useQuery({
    queryKey: [
      "listDataMappingJurusanSekolah",
      showJurusanSekolah,
      selectedSysProdi,
    ],
    queryFn: () =>
      getMappingJurusanSekolah(
        selectedSysProdi?.id_sys_periode || "",
        selectedSysProdi?.unitkerja?.uk_id || "",
      ),
    enabled: showJurusanSekolah && !!selectedSysProdi,
  });

  const filteredMappingJurusan =
    listDataMappingJurusanSekolah && listDataMappingJurusanSekolah?.length > 0
      ? listDataMappingJurusanSekolah?.filter((jurusan: JurusanSekolah) => {
          return jurusan.nama_jurusan_sekolah
            ?.toLocaleLowerCase()
            .includes(searchQueryJurusan.toLocaleLowerCase());
        })
      : [];

  useEffect(() => {
    if (isMobile) setOpenSections([]);
    else setOpenSections(["SARJANA", "VOKASI"]);
  }, [isMobile]);

  const toggleSection = (section: string) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section],
    );
  };

  // --- Helper Components ---

  const ProdiCard = ({ prodi }: { prodi: SysProgramStudi }) => (
    <div className="group relative flex flex-col justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all duration-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-slate-900 dark:text-white leading-tight">
              {prodi.unitkerja?.nama}
            </h3>
            {prodi.is_portofolio ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full mt-1 border border-amber-100 dark:border-amber-800">
                <AlertCircle className="w-3 h-3" /> Wajib Portofolio
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="w-full text-xs justify-between bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300"
        onClick={() => {
          setSelectedSysProdi(prodi);
          setShowJurusanSekolah(true);
        }}
      >
        Cek Syarat Jurusan
        <ChevronRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  );

  const SectionHeader = ({ title, subtitle, icon: Icon, sectionKey }: any) => {
    const isOpen = openSections.includes(sectionKey);
    return (
      <div
        className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="flex items-center gap-4">
          <div
            className={`p-2 rounded-full ${
              isOpen
                ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800"
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800 dark:text-slate-100">
              {title}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>
        <div
          className={`transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDown className="w-5 h-5 text-slate-400" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      {/* --- SEARCH PRODI --- */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Cari program studi..."
          value={searchQueryProdi}
          onChange={(e) => setSearchQueryProdi(e.target.value)}
          className="pl-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
        />
      </div>

      {/* --- PROGRAM VOKASI --- */}
      <div className="space-y-2">
        <SectionHeader
          title="Program Vokasi"
          subtitle="Diploma Tiga (D3) & Sarjana Terapan (D4)"
          icon={Briefcase}
          sectionKey="VOKASI"
        />

        {openSections.includes("VOKASI") && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
            {isPending ? (
              [1, 2, 3].map((i) => <SkeletonRow key={i} />)
            ) : filteredProdiVokasi?.length > 0 ? (
              filteredProdiVokasi.map((prodi) => (
                <ProdiCard key={prodi.uk_id} prodi={prodi} />
              ))
            ) : (
              <div className="col-span-full p-8 text-center rounded-xl border border-dashed bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-900/50 dark:border-slate-800 dark:text-slate-400">
                Tidak ada program studi vokasi tersedia.
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- PROGRAM SARJANA --- */}
      <div className="space-y-2">
        <SectionHeader
          title="Program Sarjana"
          subtitle="Strata Satu (S1)"
          icon={GraduationCap}
          sectionKey="SARJANA"
        />

        {openSections.includes("SARJANA") && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
            {isPending ? (
              [1, 2, 3, 4, 5, 6].map((i) => <SkeletonRow key={i} />)
            ) : filteredProdiSarjana?.length > 0 ? (
              filteredProdiSarjana.map((prodi) => (
                <ProdiCard key={prodi.uk_id} prodi={prodi} />
              ))
            ) : (
              <div className="col-span-full p-8 text-center rounded-xl border border-dashed bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-900/50 dark:border-slate-800 dark:text-slate-400">
                Tidak ada program studi sarjana tersedia.
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- MODAL SEARCH JURUSAN --- */}
      <Modal
        show={showJurusanSekolah}
        onClose={() => setShowJurusanSekolah(false)}
        className="lg:w-[50vw] md:w-[70vw] w-[95vw] max-h-[90vh] flex flex-col"
      >
        <div className="flex-none p-6 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-t-xl sticky top-0 z-10">
          <div className="mb-4">
            <Badge
              variant="outline"
              className="mb-2 text-[10px] font-normal uppercase tracking-wider text-slate-500"
            >
              Syarat Pendaftaran
            </Badge>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-snug">
              Jurusan Asal untuk{" "}
              <span className="text-blue-600">
                {selectedSysProdi?.unitkerja?.nama}
              </span>
            </h2>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Cari jurusan sekolah..."
              value={searchQueryJurusan}
              onChange={(e) => setSearchQueryJurusan(e.target.value)}
              className="pl-9 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-0 bg-slate-50/50 dark:bg-slate-950/50">
          {isPendingListDataMappingJurusanSekolah ? (
            <div className="p-6">
              <SkeletonTable />
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-slate-500 uppercase bg-slate-100 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 w-16 text-center">No</th>
                    <th className="px-6 py-3">Jurusan Sekolah</th>
                    <th className="px-6 py-3">Bentuk Pendidikan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                  {filteredMappingJurusan?.length > 0 ? (
                    filteredMappingJurusan.map(
                      (jurusan: JurusanSekolah, index: number) => (
                        <tr
                          key={index}
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        >
                          <td className="px-6 py-3 text-center text-slate-400 font-mono text-xs">
                            {index + 1}
                          </td>
                          <td className="px-6 py-3 font-medium text-slate-700 dark:text-slate-200">
                            {jurusan.nama_jurusan_sekolah}
                          </td>
                          <td className="px-6 py-3 text-slate-500 dark:text-slate-400 text-xs">
                            {jurusan.bentuk_pendidikan ? (
                              <div className="flex flex-wrap gap-1">
                                {(
                                  JSON.parse(
                                    String(jurusan.bentuk_pendidikan),
                                  ) as string[]
                                ).map((bp, i) => (
                                  <span
                                    key={i}
                                    className="inline-block px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700"
                                  >
                                    {bp}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ),
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <AlertCircle className="w-8 h-8 text-slate-300" />
                          <p>Jurusan tidak ditemukan.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex-none p-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-xl">
          <Button
            variant="outline"
            onClick={() => setShowJurusanSekolah(false)}
            className="w-full"
          >
            Tutup
          </Button>
        </div>
      </Modal>
    </div>
  );
}
